import { Space } from 'antd';
import { get, keyBy, sortBy, values, differenceBy } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ProjectId } from 'api/models';
import { Tag } from 'components/atoms/tag';
import { Board } from 'components/molecules/board';
import { Column } from './column';
import { CardDimension, COLUMN_HEADER_HEIGHT } from './common';
import { useBoardContext } from './context';
import { Collapse, SwimlaneHeaderSubTitle } from './styled';
import type { BoardProps, Column as ColumnT } from './types';
import { generateDroppableId } from './utils';

type Props = Pick<
  BoardProps,
  | 'swimlanes'
  | 'columns'
  | 'onCreate'
  | 'size'
  | 'currentProjectId'
  | 'isColumnHidden'
>;

const height = (taskCount = 0): number => {
  const itemHeight = CardDimension.height + 8;
  const headerHeight = COLUMN_HEADER_HEIGHT + 16;

  switch (taskCount) {
    case 0:
      return itemHeight + headerHeight;
    case 1:
      return itemHeight * 2 + headerHeight;
    case 2:
    default:
      return itemHeight * 3 + headerHeight;
  }
};

export const BoardOrchestrator: FC<Props> = ({
  swimlanes,
  columns: givenColumns,
  onCreate,
  size,
  currentProjectId,
  isColumnHidden,
}) => {
  const { state } = useBoardContext();
  const columns = sortBy(givenColumns, 'position');

  const columnDescription = swimlanes.map(({ colId }) => {
    const cols = get(state, colId, {}) as Record<string, string[]>;
    const meta = Object.entries(cols).reduce(
      (prev, next) => {
        const count = next[1].length;

        return {
          total: prev.total + count,
          highest: Math.max(count, prev.highest),
        };
      },
      { total: 0, highest: 0 },
    );

    return {
      colId,
      meta,
    };
  });

  const swimlanesMeta = useMemo(() => {
    return keyBy(columnDescription, 'colId');
  }, [columnDescription]);

  const rowKeys = useMemo(
    () => swimlanes.map(({ colId }) => colId),
    [swimlanes],
  );

  const orderedSwimlanes = useMemo((): ColumnT[] => {
    const withItems = swimlanes.filter(
      (swimlane) =>
        values(get(state, swimlane.colId, {})).flat(2).filter(Boolean).length,
    );
    const withoutItems = differenceBy(swimlanes, withItems, 'colId');

    return [...withItems, ...withoutItems];
  }, [swimlanes, state]);

  const filteredSwimLanes = isColumnHidden
    ? orderedSwimlanes.filter(
        (row) => swimlanesMeta[row.colId].meta.total !== 0,
      )
    : orderedSwimlanes;

  const [activeKeys, setActiveKeys] = useState<string[]>(rowKeys);

  /* TODO:: move to context state and it should be based on rows isCollapsed: true/false */
  useDeepCompareEffect(() => {
    setActiveKeys(rowKeys);
  }, [rowKeys]);

  const header = (row: ColumnT, count: number) => (
    <Space align="center" size={8}>
      <Tag color={row.color}>{row.label}</Tag>
      <SwimlaneHeaderSubTitle>
        <FormattedMessage id="tasks.count" values={{ count }} />
      </SwimlaneHeaderSubTitle>
    </Space>
  );

  return swimlanes.length > 0 ? (
    <Collapse
      ghost
      activeKey={activeKeys}
      onChange={(_keys) =>
        Array.isArray(_keys) ? setActiveKeys(_keys) : setActiveKeys([_keys])
      }
    >
      {filteredSwimLanes.map((row) => (
        <Collapse.Panel
          showArrow={false}
          key={row.colId}
          header={header(row, swimlanesMeta[row.colId].meta.total)}
        >
          <Board.Body.Columns
            style={{
              height: height(swimlanesMeta[row.colId].meta.highest) + 16,
              paddingLeft: 'var(--spacing-03)',
            }}
          >
            {columns.map((column) => {
              const derivedCurrentProjectId =
                currentProjectId ||
                (row.dataKey === 'project'
                  ? ProjectId(Number(row.value))
                  : null);

              return (
                <Column
                  key={generateDroppableId(row.colId, column.colId)}
                  height={height(swimlanesMeta[row.colId].meta.highest)}
                  column={column}
                  items={get(state, [row.colId, column.colId], [])}
                  rowId={row.colId}
                  row={row}
                  currentProjectId={derivedCurrentProjectId}
                  onCreate={
                    onCreate
                      ? (record, col) => onCreate(record, col, row)
                      : null
                  }
                />
              );
            })}
          </Board.Body.Columns>
        </Collapse.Panel>
      ))}
    </Collapse>
  ) : (
    <Board.Body.Columns style={{ paddingLeft: 'var(--left-spacing)' }}>
      {columns.map((column) => {
        return (
          <Column
            key={column.colId}
            height={size.height - 16}
            column={column}
            items={get(state, ['-1', column.colId], [])}
            rowId="-1"
            row={null}
            onCreate={
              onCreate
                ? (record, col) => onCreate(record, col, undefined)
                : null
            }
            currentProjectId={currentProjectId}
          />
        );
      })}
    </Board.Body.Columns>
  );
};
